import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Sidebar } from "@/components/layout/Sidebar";
import { TopBar } from "@/components/layout/TopBar";
import { queryClient } from "@/lib/api/query-client";
import { ErrorBoundary } from "./components/error/ErrorBoundary";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { PATHS } from "@/lib/paths";

// Lazy load pages
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const UserManagementPage = lazy(
  () => import("@/features/users/UserManagementPage")
);
const BusinessKybPage = lazy(() => import("@/features/users/BusinessKybPage"));
const ProductControlPage = lazy(
  () => import("@/features/products/ProductControlPage")
);
const ProductDetailsPage = lazy(
  () => import("@/features/products/pages/ProductDetailsPage")
);
const ProductPlanDetailsPage = lazy(
  () => import("@/features/products/pages/ProductPlanDetailsPage")
);
const LoanManagementPage = lazy(
  () => import("@/features/loans/LoanManagementPage")
);
const LoanDetailPage = lazy(() => import("@/features/loans/LoanDetailPage"));
const AnalyticsPage = lazy(() => import("@/features/analytics/AnalyticsPage"));
const SupportCentrePage = lazy(
  () => import("@/features/support/SupportCentrePage")
);
const AIHubPage = lazy(() => import("@/features/ai/AIHubPage"));
const AgentPortalPage = lazy(() => import("@/features/agents/AgentPortalPage"));
const SecurityPage = lazy(() => import("@/features/security/SecurityPage"));
const LoginPage = lazy(() => import("@/features/auth/LoginPage"));
const UnauthorizedPage = lazy(() => import("@/features/auth/UnauthorizedPage"));
import { NotFoundPage } from "@/components/NotFoundPage";
import { MobileWarning } from "@/components/MobileWarning";
import { useAuthStore } from "./store/auth";
import { useProfile } from "./features/auth/hooks/useAuth";
import RequestPasswordResetPage from "./features/auth/RequestPasswordResetPage";
import ResetPasswordPage from "./features/auth/ResetPasswordPage";
const SettingsPage = lazy(() => import("./features/security/SettingsPage"));
import AcceptInvitationPage from "./features/auth/AcceptInvitationPage";
import { PERMISSIONS } from "./lib/permissions";
import { ConnectivityGate } from "./components/common/ConnectivityGate";
const NotificationsPage = lazy(
  () => import("@/features/notifications/pages/NotificationsPage")
);
const UserDetailPage = lazy(() => import("./features/users/UserDetailPages"));
const KYCAndCompliancePage = lazy(
  () => import("./features/kyc/pages/KycAndCompliancePage")
);
const KYCDetailPage = lazy(() => import("./features/kyc/pages/KycDetailPage"));
const KybDetailPage = lazy(() => import("./features/kyc/pages/KybDetailPage"));
const AccountManagementPage = lazy(
  () => import("@/features/accounts/pages/AccountManagementPage")
);
const AccountDetailPage = lazy(
  () => import("@/features/accounts/pages/AccountDetailPage")
);
const ApprovalsPage = lazy(
  () => import("@/features/approvals/pages/ApprovalsPage")
);
const TransactionsPage = lazy(
  () => import("@/features/transactions/pages/TransactionsPage")
);
const ProductDashboardRouter = lazy(
  () => import("@/features/products/ProductDashboardRouter")
);
const ProductPlanDetailsRouter = lazy(
  () => import("@/features/products/ProductPlanDetailsRouter")
);

const LoadingFallback = () => (
  <div className="flex h-full w-full items-center justify-center">
    <div className="text-xl font-semibold">Loading...</div>
  </div>
);

const MainLayout = () => {
  const { data: profile, isError } = useProfile();
  const setUser = useAuthStore((state) => state.setUser);
  const logout = useAuthStore((state) => state.logout);

  useEffect(() => {
    if (profile) {
      setUser(profile);
    }
  }, [profile, setUser]);

  useEffect(() => {
    if (isError) {
      logout();
    }
  }, [isError, logout]);

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-y-auto p-2 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      {/* <MobileWarning /> */}
      <ConnectivityGate>
        <BrowserRouter>
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path={PATHS.login} element={<LoginPage />} />
                <Route
                  path={PATHS.unauthorized}
                  element={<UnauthorizedPage />}
                />
                <Route
                  path={PATHS.acceptInvitation}
                  element={<AcceptInvitationPage />}
                />
                <Route
                  path={PATHS.forgotPassword}
                  element={<RequestPasswordResetPage />}
                />
                <Route
                  path={PATHS.resetPassword}
                  element={<ResetPasswordPage />}
                />

                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route
                    path={PATHS.approvals}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.APPROVALS_READ]}
                      >
                        <ApprovalsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.users.root}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.USERS_READ]}
                      >
                        <UserManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.users.detail}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.USERS_READ]}
                      >
                        <UserDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.accounts.root}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.ACCOUNTS_READ]}
                      >
                        <AccountManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.accounts.detail}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.ACCOUNTS_READ]}
                      >
                        <AccountDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.transactions}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.TRANSACTIONS_READ]}
                      >
                        <TransactionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.compliance.root}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.KYC_READ]}
                      >
                        <KYCAndCompliancePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.compliance.kycDetail}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.KYC_READ]}
                      >
                        <KYCDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.compliance.kybDetail}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.KYC_READ]}
                      >
                        <KybDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.products.root}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.PRODUCTS_READ]}
                      >
                        <ProductControlPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.products.details}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.PRODUCTS_READ]}
                      >
                        <ProductDashboardRouter />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.products.planDetails}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.PRODUCTS_READ]}
                      >
                        <ProductPlanDetailsRouter />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.loans}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.LOANS_READ]}
                      >
                        <LoanManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.loanDetail}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.LOANS_READ]}
                      >
                        <LoanDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.analytics}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.REPORTS_READ]}
                      >
                        <AnalyticsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.support}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.SUPPORT_TICKETS_READ]}
                      >
                        <SupportCentrePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.notifications}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.NOTIFICATIONS_READ]}
                      >
                        <NotificationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.ai}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.AI_SETTINGS_READ]}
                      >
                        <AIHubPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.agents}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.AGENTS_READ]}
                      >
                        <AgentPortalPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path={PATHS.security}
                    element={
                      <ProtectedRoute
                        requiredPermissions={[PERMISSIONS.ADMINS_READ]}
                      >
                        <SecurityPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path={PATHS.settings} element={<SettingsPage />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </ConnectivityGate>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
